import { Navigate, useSearchParams } from 'react-router-dom';
import { useGetAccountQuery } from '@/features/account/accountApi';
import { Button } from '@/components/ui/button';
import { CenteredCardLayout } from '@/components/ui/centered-card-layout';
import { ErrorState } from '@/components/ui/error-state';
import { ssoLoginUrl } from '@/services/apiBase';

function startSsoLogin(): void {
  window.location.href = ssoLoginUrl();
}

/**
 * The TCIT brand above the card (`CenteredCardLayout`'s contract, §5.5: "the
 * logo does go here, not in the AppShell" — that one carries the PRODUCT's
 * wordmark).
 *
 * There is no logo asset in the repo (neither an SVG nor a `Logo` component),
 * so this is a text mark composed only from semantic roles: `bg-brand` (the
 * brand surface, the one brand role that compiles —
 * `text-brand`/`border-brand` do not, see theme.css) and `text-fg-inverse`
 * for the text over that background. If a real asset appears, this node is what
 * has to be replaced.
 */
function TcitBrandMark(): React.JSX.Element {
  return (
    <span className="inline-flex items-center rounded-full bg-brand px-inset py-inset-sm">
      <span className="text-kicker uppercase text-fg-inverse">TCIT</span>
    </span>
  );
}

/**
 * Sign-in through SSO (TCIT Doorkeeper). The button redirects to the backend,
 * which in turn redirects to the OAuth provider and, on the callback, sets the
 * session cookie (see docs/FRONTEND_PLAN.md §3.1).
 *
 * With a session already in place there is no point showing the button, so the
 * account is asked for: a 200 on `GET /api/account` means the cookie works and
 * the user should be sent into the app. The query is the same one the layout
 * makes and RTK Query shares it, so it adds no extra request.
 *
 * The form renders FROM THE FIRST FRAME, without waiting for the query to
 * resolve. Putting a «Cargando…» there in the meantime sounds tidy and is the
 * wrong way round: whoever arrives at `/login` almost always has NO session, so
 * the universal case pays a blank screen for the rare case's benefit. And a
 * page that renders nothing while loading has no focusable element: the `Tab`
 * of the focus-ring test lands on `<body>` and the test fails, which is exactly
 * how this was detected. That test is not brittle — it was warning that the
 * page was coming out empty.
 *
 * The cost on the other side is that someone with a session sees the button for
 * an instant before the redirect. It is the right trade: a flicker in the rare
 * case against a blank wait in the common one.
 *
 * This view's real states (inventory §1, row 1):
 * - **Data** (there is a session): `GET /api/account` resolves 200 -> redirect
 *   to `/`.
 * - **Default** (no session, the explicit 401 included): the button is offered.
 *   There is no "empty" distinct from this — there is no collection to filter.
 * - **Error**: the `error` the OAuth provider leaves in the callback's query
 *   string (`GET /auth/callback` -> `code`/`state`/`error`) is shown in the
 *   card with `ErrorState`, not as a separate error page (contract §5.1 / row 1
 *   of the inventory). The button stays available below to retry.
 */
export function LoginPage(): React.JSX.Element {
  const { data: account } = useGetAccountQuery();
  const [searchParams] = useSearchParams();
  const callbackError = searchParams.get('error');

  if (account) {
    return <Navigate to="/" replace />;
  }

  return (
    <CenteredCardLayout
      title="Iniciar sesión"
      description="Usa tu cuenta de TCIT para continuar."
      brand={<TcitBrandMark />}
    >
      <div className="flex flex-col gap-stack">
        {callbackError ? (
          <ErrorState
            error={callbackError}
            title="No se pudo iniciar sesión"
            onRetry={startSsoLogin}
          />
        ) : null}
        <Button className="w-full" onClick={startSsoLogin}>
          Iniciar sesión con SSO
        </Button>
      </div>
    </CenteredCardLayout>
  );
}
